import { useState, type ComponentType } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/PageHeader";
import { QueryErrorBanner } from "../components/QueryErrorBanner";
import { KpiTile } from "../components/intelligence/KpiTile";
import { AttentionPanel } from "../components/intelligence/AttentionPanel";
import { SystemHero, HeroNumber } from "../components/intelligence/SystemHero";
import { fmtDateTime } from "../lib/format";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import { IconSync, IconRefresh, IconCheck, IconX, IconDatabase, IconClock, IconAlertTriangle, IconCalendar } from "../components/icons";
import {
  AREAS, ago, cronTime, entityHealth, entityInfo, explainError, fmtWhen, nextCycle, summarize, syncAttention,
  type Health, type SyncOverview,
} from "../lib/sync";

interface DataSource {
  id: string;
  name: string;
  connectionStatus: string;
  lastSyncAt: string | null;
  host: string | null;
  database: string | null;
  externalCompanyCodes: number[];
}

const TONE: Record<Health["tone"], string> = {
  ok: "bg-tm-50 text-tm-700",
  warn: "bg-warning-50 text-warning-700",
  bad: "bg-danger-50 text-danger-700",
  muted: "bg-gray-100 text-gray-500",
};

// ── Disparo manual ────────────────────────────────────────────────────────────
function TriggerModal({ dataSources, onClose }: { dataSources: DataSource[]; onClose: (started: boolean) => void }) {
  const [dsId, setDsId] = useState(dataSources[0]?.id ?? "");
  const [force, setForce] = useState(false);
  const codes = dataSources.find((d) => d.id === dsId)?.externalCompanyCodes ?? [];
  const trigger = useMutation({
    mutationFn: () => api.post("/sync/trigger", { dataSourceId: dsId, externalCompanyCodes: codes, force }),
    onSuccess: () => onClose(true),
  });
  // 409 = já existe um ciclo rodando; a API explica e nada é disparado.
  const error = trigger.error instanceof ApiError ? trigger.error.message : trigger.error ? "Não foi possível iniciar a sincronização." : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="sync-modal-title">
      <div className="mx-4 w-full max-w-md space-y-5 rounded-2xl bg-white p-6 shadow-card-lg">
        <div className="flex items-center justify-between">
          <h2 id="sync-modal-title" className="text-base font-semibold text-gray-900">Sincronizar agora</h2>
          <button onClick={() => onClose(false)} className="btn-ghost p-1" aria-label="Fechar"><IconX className="h-4 w-4" /></button>
        </div>
        <p className="text-sm text-gray-600">Busca no ERP o que mudou desde o último ciclo, sem esperar a próxima meia hora. Leva alguns minutos.</p>
        {dataSources.length > 1 && <label className="block text-xs font-medium text-gray-600">Banco de dados
          <select value={dsId} onChange={(e) => setDsId(e.target.value)} className="mt-1 w-full rounded-lg border border-surface-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-tm-500">
            {dataSources.map((ds) => <option key={ds.id} value={ds.id}>{ds.name}</option>)}
          </select>
        </label>}
        <div className="rounded-lg bg-surface-muted px-4 py-3 text-xs text-gray-600">
          {codes.length ? <>Empresas do ERP incluídas: <span className="font-mono font-semibold text-gray-800">{codes.join(", ")}</span></> : <span className="text-warning-700">Este banco não tem códigos de empresa configurados.</span>}
        </div>
        <label className="flex cursor-pointer items-start gap-2">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} className="mt-0.5 h-4 w-4 accent-tm-500" />
          <span className="text-sm text-gray-700">Recarregar tudo desde o início<span className="block text-xs text-gray-500">Bem mais demorado. Use só se faltar dado antigo no BI.</span></span>
        </label>
        {error && <p role="alert" className="flex items-center gap-2 rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-xs text-danger-700"><IconAlertTriangle className="h-4 w-4 flex-shrink-0" />{error}</p>}
        <div className="flex gap-3">
          <button onClick={() => onClose(false)} className="btn-secondary flex-1">Cancelar</button>
          <button onClick={() => trigger.mutate()} disabled={trigger.isPending || !codes.length} className="btn-primary flex-1">
            <IconSync className={`h-4 w-4 ${trigger.isPending ? "animate-spin" : ""}`} />{trigger.isPending ? "Iniciando…" : "Sincronizar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Telemetria GOBRAX ─────────────────────────────────────────────────────────

interface GobraxSyncStatus {
  enabled: boolean;
  cron: string;
  running: { trigger: "cron" | "manual"; startedAt: string } | null;
  lastRun: {
    trigger: "cron" | "manual";
    startedAt: string;
    finishedAt: string;
    ok: boolean;
    summary: string;
    result: { semCadastro: string[]; odometroRegressoes: string[] } | null;
  } | null;
  lastSyncedAt: string | null;
  lastReadingAt: string | null;
  platesWithOdometer: number;
}

/**
 * Coleta manual do odômetro da GOBRAX.
 *
 * O botão pede confirmação em dois passos porque a coleta é um LOGIN na conta
 * da GOBRAX do cliente, e há indício de sessão única por conta: quem estiver
 * logado lá pode ser desconectado. O cron roda de madrugada por isso; o
 * disparo manual é para quando o dado é necessário antes — e quem clica
 * precisa saber o que está fazendo.
 */
function GobraxCard() {
  const queryClient = useQueryClient();
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const { data: st } = useQuery<GobraxSyncStatus>({
    queryKey: ["gobrax", "sync-status"],
    queryFn: () => api.get("/gobrax/sync/status"),
    staleTime: 10_000,
    // Enquanto roda, acompanha de perto; parado, o status muda uma vez por dia.
    refetchInterval: (q) => (q.state.status === "error" ? false : q.state.data?.running ? 4_000 : 60_000),
    retryOnMount: false,
  });

  const disparar = async () => {
    setErro(null);
    setConfirmando(false);
    try {
      await api.post("/gobrax/sync/run");
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["gobrax", "sync-status"] }), 1_000);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao disparar a coleta.");
    }
  };

  if (!st) return null;

  const rodando = st.running != null;
  const ultimo = st.lastRun;

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${st.enabled ? "bg-tm-50 text-tm-600" : "bg-gray-100 text-gray-400"}`}>
          <IconSync className={`w-6 h-6 ${rodando ? "animate-spin" : ""}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900">Telemetria GOBRAX: odômetro</p>
            {!st.enabled && <span className="badge-gray">Desligada</span>}
            {st.enabled && rodando && <span className="badge-amber">Coletando…</span>}
            {st.enabled && !rodando && ultimo && (
              <span className={ultimo.ok ? "badge-green" : "badge-red"}>{ultimo.ok ? "Sucesso" : "Falhou"}</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {st.enabled
              ? `Automático às ${cronTime(st.cron)} · não roda no deploy`
              : "Preencha GOBRAX_ENABLED, BASE_URL, USER e PASSWORD na stack para ligar."}
          </p>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs text-gray-600">
            <p><span className="text-gray-400">Última coleta:</span> {st.lastSyncedAt ? fmtDateTime(st.lastSyncedAt) : "nunca"}</p>
            <p><span className="text-gray-400">Leitura mais recente:</span> {st.lastReadingAt ? fmtDateTime(st.lastReadingAt) : "—"}</p>
            <p><span className="text-gray-400">Placas com odômetro:</span> {st.platesWithOdometer}</p>
          </div>
          {ultimo && (
            <p className={`text-xs mt-2 ${ultimo.ok ? "text-gray-500" : "text-danger-600"}`}>
              {ultimo.trigger === "manual" ? "Manual" : "Agendada"} em {fmtDateTime(ultimo.startedAt)}: {ultimo.summary}
            </p>
          )}
          {ultimo?.result && ultimo.result.semCadastro.length > 0 && (
            <p className="text-xs text-warning-700 mt-1">
              Fora do cadastro: {ultimo.result.semCadastro.join(", ")}
            </p>
          )}
          {ultimo?.result && ultimo.result.odometroRegressoes.length > 0 && (
            <p className="text-xs text-warning-700 mt-1">
              Odômetro andou para trás (não gravado): {ultimo.result.odometroRegressoes.join(", ")}
            </p>
          )}
          {erro && <p className="text-xs text-danger-600 mt-2">{erro}</p>}
        </div>
        <div className="flex-shrink-0">
          {!confirmando && (
            <button
              onClick={() => setConfirmando(true)}
              disabled={!st.enabled || rodando}
              className="btn-primary"
            >
              {rodando ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <IconRefresh className="w-4 h-4" />
              )}
              {rodando ? "Coletando…" : "Coletar agora"}
            </button>
          )}
        </div>
      </div>

      {confirmando && (
        <div className="flex items-start gap-3 bg-warning-50 border border-warning-200 text-warning-800 px-4 py-3 rounded-xl text-sm">
          <IconAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">A coleta faz login na conta da GOBRAX do cliente.</p>
            <p className="text-xs mt-1 text-warning-700">
              Se alguém estiver usando a GOBRAX agora, pode ser desconectado. Leva de 30 s a 2 min, e
              o resultado aparece neste card. Prefira fora do expediente.
            </p>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setConfirmando(false)} className="btn-secondary py-1 text-xs">Cancelar</button>
              <button onClick={disparar} className="btn-primary py-1 text-xs">Entendi, coletar agora</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function SyncPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" && !user.isDemo;
  const qc = useQueryClient();
  const [tab, setTab] = useState<"tables" | "cycles" | "schedules">("tables");
  const [showModal, setShowModal] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const overview = useQuery<SyncOverview>({
    queryKey: ["sync", "overview"],
    queryFn: () => api.get("/sync/overview"),
    staleTime: 15_000,
    // Acompanha o ciclo enquanto a leitura funciona; com erro, para de insistir
    // (um 403 a cada 30 s ainda fazia o app reler a sessão e redesenhar a tela).
    refetchInterval: (q) => (q.state.status === "error" ? false : 30_000),
    retryOnMount: false,
  });
  const ds = useQuery<{ items: DataSource[] }>({ queryKey: ["data-sources"], queryFn: () => api.get("/data-sources"), staleTime: 60_000, enabled: canEdit, retryOnMount: false });

  const now = new Date();
  const o = overview.data;
  const sum = o ? summarize(o, now) : null;
  const loading = overview.isLoading;
  const dataSources = ds.data?.items ?? [];
  const connected = dataSources.filter((d) => d.connectionStatus === "ok");
  const problems = sum ? sum.broken.length + sum.late.length : 0;
  const next = nextCycle(now);
  const hhmm = (d: Date) => d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  const closeModal = (started: boolean) => {
    setShowModal(false);
    if (started) {
      setNotice("Sincronização iniciada. Acompanhe na aba Ciclos; leva alguns minutos.");
      setTab("cycles");
      setTimeout(() => qc.invalidateQueries({ queryKey: ["sync", "overview"] }), 2_000);
    }
  };

  return <>
    {showModal && <TriggerModal dataSources={connected.length ? connected : dataSources} onClose={closeModal} />}
    <div className="space-y-6">
      <PageHeader
        title="Sincronização"
        subtitle="Como os dados do ERP e da Finatto chegam ao BI"
        actions={canEdit && !ds.error ? <button onClick={() => setShowModal(true)} disabled={!dataSources.length} className="btn-primary"><IconSync className="h-4 w-4" />Sincronizar agora</button> : undefined}
      />

      {overview.error && <QueryErrorBanner what="a situação da sincronização" error={overview.error} onRetry={() => overview.refetch()} retrying={overview.isFetching} />}
      {canEdit && ds.data && !dataSources.length && <p role="alert" className="flex items-center gap-2 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-700"><IconAlertTriangle className="h-4 w-4 flex-shrink-0" />Nenhum banco de dados configurado. Cadastre a conexão com o ERP em Configurações → Bancos de Dados.</p>}
      {notice && <p role="status" className="flex items-center gap-2 rounded-lg border border-tm-100 bg-tm-50 px-4 py-3 text-sm text-tm-800"><IconCheck className="h-4 w-4 flex-shrink-0" />{notice}<button className="ml-auto text-xs underline" onClick={() => setNotice(null)}>Fechar</button></p>}

      {!overview.error && <SystemHero loading={loading}
        status={sum ? (sum.status.tone === "bad" ? "critical" : sum.status.tone === "warn" || problems ? "warning" : "ok") : "ok"}
        statusText={sum ? (problems ? `${problems} tabela${problems > 1 ? "s" : ""} com problema` : "tabelas em dia") : undefined}
        headline={sum ? (sum.lastUpdate
          ? <>{sum.status.tone === "bad" ? "Os dados do ERP pararam de chegar" : "Os dados do ERP chegaram"} <HeroNumber>{ago(sum.lastUpdate, now)}</HeroNumber>.</>
          : <>O BI ainda não recebeu dados do ERP.</>) : null}
        detail={sum ? `${sum.status.detail}${sum.status.tone !== "bad" ? ` Próximo ciclo às ${hhmm(next)}.` : ""}` : undefined} />}

      {!overview.error && <AttentionPanel points={o && sum ? syncAttention(o, sum, now) : []} loading={loading}
        empty={{ title: "Nada pede ação", text: "Todas as tabelas do ERP e da Finatto estão em dia e o último ciclo terminou sem falha." }} />}

      {!(overview.error && !o) && <>
      <section aria-labelledby="indicadores-titulo" className="space-y-3">
        <h2 id="indicadores-titulo" className="font-display font-semibold text-lg text-gray-900">Indicadores da sincronização</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <KpiTile label="Última atualização" value={sum?.lastUpdate ? ago(sum.lastUpdate, now) : "—"} loading={loading}
            tone={sum?.status.tone === "bad" ? "critical" : null}
            footnote={sum?.lastUpdate ? fmtWhen(sum.lastUpdate) : "Nenhuma sincronização registrada"} />
          <KpiTile label="Ciclos nas últimas 24h" value={sum ? String(sum.cycles24h) : "—"} loading={loading}
            tone={sum ? (sum.cycles24h === 0 ? "critical" : sum.failedCycles24h || sum.cycles24h < 44 ? "warning" : null) : null}
            footnote={sum ? `${sum.failedCycles24h ? `${sum.failedCycles24h} com falha` : "Nenhum com falha"} · o normal são 48 por dia` : undefined} />
          <KpiTile label="Tabelas com problema" value={sum ? `${problems} de ${sum.tracked}` : "—"} loading={loading}
            tone={problems ? "warning" : null}
            footnote={sum ? (problems ? `${sum.broken.length} com falha · ${sum.late.length} atrasada${sum.late.length === 1 ? "" : "s"}` : "Todas em dia") : undefined} />
          <KpiTile label="Próximo ciclo" value={hhmm(next)} loading={loading}
            footnote={`A cada 30 min · carga completa às ${cronTime(o?.schedules.nightlyFull ?? "10 4 * * *")}`} />
        </div>
      </section>

      <h2 className="font-display font-semibold text-lg text-gray-900">Detalhes</h2>
      <section className="card overflow-hidden">
        <div className="flex flex-wrap gap-1 border-b border-surface-border px-5" role="tablist" aria-label="Sincronização">
          {([["tables", "Tabelas"], ["cycles", "Ciclos"], ["schedules", "Agendamentos"]] as const).map(([key, label]) =>
            <button key={key} role="tab" aria-selected={tab === key} aria-controls={`sync-${key}`} id={`tab-${key}`} onClick={() => setTab(key)} className={`border-b-2 px-4 py-4 text-sm font-semibold ${tab === key ? "border-tm-500 text-tm-700" : "border-transparent text-gray-500 hover:text-gray-900"}`}>{label}</button>)}
        </div>
        <div role="tabpanel" id={`sync-${tab}`} aria-labelledby={`tab-${tab}`} className="p-5">
          {!o ? <p role="status" className="text-sm text-gray-500">{overview.error ? "Sem dados." : "Carregando…"}</p>
            : tab === "tables" ? <Tables o={o} now={now} />
            : tab === "cycles" ? <Cycles o={o} />
            : <Schedules o={o} />}
        </div>
      </section>
      </>}
    </div>
  </>;
}

function Pill({ h }: { h: Health }) {
  return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${TONE[h.tone]}`}>{h.label}</span>;
}

function Tables({ o, now }: { o: SyncOverview; now: Date }) {
  return <div className="space-y-6">
    {AREAS.map((area) => {
      const rows = o.entities.filter((e) => entityInfo(e.entity).area === area);
      if (!rows.length) return null;
      return <div key={area}>
        <h3 className="mb-2 text-xs font-semibold text-gray-600">{area}</h3>
        <div className="overflow-x-auto rounded-xl border border-surface-border">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-surface-muted text-xs text-gray-500"><tr>
              <th className="p-3 pl-4">Tabela</th><th className="p-3">Situação</th><th className="p-3">Última atualização</th><th className="p-3">Carga completa</th><th className="p-3 text-right">Falhas em 24h</th><th className="p-3 pr-4">Último erro</th>
            </tr></thead>
            <tbody className="divide-y divide-surface-border">{rows.map((e) => {
              const info = entityInfo(e.entity);
              // Erro anterior ao último sucesso já passou; a contagem de 24h continua mostrando que houve.
              const err = e.lastErrorAt && (!e.lastSuccessAt || e.lastErrorAt > e.lastSuccessAt) ? explainError(e.lastError) : null;
              return <tr key={e.entity} className="align-top">
                <td className="p-3 pl-4"><p className="font-medium text-gray-900">{info.label}</p>{info.feeds && <p className="text-xs text-gray-500">{info.feeds}</p>}</td>
                <td className="p-3"><Pill h={entityHealth(e, now)} /></td>
                <td className="whitespace-nowrap p-3 tabular-nums">{e.lastSuccessAt ? <>{ago(e.lastSuccessAt, now)}<span className="block text-xs text-gray-500">{fmtWhen(e.lastSuccessAt)}</span></> : "—"}</td>
                <td className="whitespace-nowrap p-3 tabular-nums text-gray-600">{e.lastFullAt ? fmtWhen(e.lastFullAt) : "—"}</td>
                <td className={`p-3 text-right tabular-nums ${e.failures24h ? "font-semibold text-danger-600" : "text-gray-500"}`}>{e.failures24h}</td>
                <td className="p-3 pr-4 text-xs text-gray-600" title={e.lastError ?? undefined}>{err ? <>{err}{e.lastErrorAt && <span className="block text-gray-400">{fmtWhen(e.lastErrorAt)}</span>}</> : "—"}</td>
              </tr>;
            })}</tbody>
          </table>
        </div>
      </div>;
    })}
  </div>;
}

function Cycles({ o }: { o: SyncOverview }) {
  const list = useProgressiveList(o.cycles);
  if (!o.cycles.length) return <p className="text-sm text-gray-500">Nenhum ciclo nos últimos 7 dias.</p>;
  return <div className="overflow-hidden rounded-xl border border-surface-border">
    <div className="overflow-x-auto"><table className="w-full min-w-[720px] text-left text-sm">
      <thead className="bg-surface-muted text-xs text-gray-500"><tr><th className="p-3 pl-4">Início</th><th className="p-3">Duração</th><th className="p-3">Resultado</th><th className="p-3 text-right">Execuções</th><th className="p-3 pr-4">O que falhou</th></tr></thead>
      <tbody className="divide-y divide-surface-border">{list.visible.map((c) => {
        const min = c.finishedAt ? Math.max(1, Math.round((new Date(c.finishedAt).getTime() - new Date(c.startedAt).getTime()) / 60_000)) : null;
        const h: Health = c.running ? { tone: "ok", label: "Rodando" } : c.interrupted ? { tone: "bad", label: "Interrompido" } : c.failed ? { tone: c.failed === c.jobs ? "bad" : "warn", label: c.failed === c.jobs ? "Falhou" : "Parcial" } : { tone: "ok", label: "Concluído" };
        return <tr key={c.slot} className="align-top">
          <td className="whitespace-nowrap p-3 pl-4 tabular-nums">{fmtWhen(c.startedAt)}{c.full && <span className="ml-2 rounded-full bg-info-50 px-2 py-0.5 text-xs font-semibold text-info-700">carga completa</span>}</td>
          <td className="whitespace-nowrap p-3 tabular-nums text-gray-600">{min != null ? `${min} min` : "—"}</td>
          <td className="p-3"><Pill h={h} /></td>
          <td className="p-3 text-right tabular-nums text-gray-600">{c.failed ? `${c.failed} de ${c.jobs}` : c.jobs}</td>
          <td className="p-3 pr-4 text-xs text-gray-600" title={c.lastError ?? undefined}>{c.interrupted && !c.failed ? <>Interrompido por reinício do BI<span className="block text-gray-400">o ciclo seguinte refaz o trabalho</span></> : c.failed ? <>{explainError(c.lastError)}<span className="block text-gray-400">{c.failedEntities.map((e) => entityInfo(e).label).join(", ")}</span></> : "—"}</td>
        </tr>;
      })}</tbody>
    </table></div>
    <div className="border-t border-surface-border"><LoadMoreBar list={list} label="ciclos" capped={false} /></div>
  </div>;
}

function Schedules({ o }: { o: SyncOverview }) {
  const items: { Icon: ComponentType<{ className?: string }>; title: string; when: string; text: string }[] = [
    { Icon: IconSync, title: "Atualização do ERP", when: "A cada 30 min (:00 e :30)", text: "Busca o que mudou no ERP para todas as empresas configuradas. É o que mantém os painéis atualizados durante o dia." },
    { Icon: IconDatabase, title: "Carga completa do ERP", when: `Todo dia às ${cronTime(o.schedules.nightlyFull)}`, text: "Recarrega contas a receber e a pagar, CT-e, cartas frete, viagens e abastecimentos inteiros, para pegar baixas e lançamentos retroativos que o ciclo de 30 min não vê." },
    { Icon: IconCalendar, title: "Pneus (Finatto)", when: o.schedules.finatto ? `Todo dia às ${cronTime(o.schedules.finatto)}` : "Desligado", text: "Cadastro, movimentações, custos, baixas e pneus montados por placa, direto da Finatto." },
    { Icon: IconClock, title: "Qualidade dos dados", when: "Todo dia às 02:00", text: "Procura lançamentos do ERP com inconsistência. O resultado fica na tela Qualidade dos Dados." },
  ];
  return <div className="space-y-4">
    <div className="grid gap-3 lg:grid-cols-2">
      {items.map((it) => <article key={it.title} className="flex gap-3 rounded-xl border border-surface-border p-4">
        <span className="h-fit rounded-lg bg-tm-50 p-2 text-tm-600"><it.Icon className="h-5 w-5" /></span>
        <div className="min-w-0"><h3 className="text-sm font-semibold text-gray-900">{it.title}</h3><p className="text-xs font-medium text-tm-700">{it.when}</p><p className="mt-1 text-xs leading-relaxed text-gray-600">{it.text}</p></div>
      </article>)}
    </div>
    <GobraxCard />
  </div>;
}
